import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowUpRight, Camera, PencilLine, Type, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/platform/ui/card";
import { GradingProgress } from "@/platform/ui/grading-progress";
import { showCorrectionError } from "@/platform/billing/errors";
import { useSession } from "@/platform/auth/use-session";
import {
  gradeEssay,
  listEssayPrompts,
  uploadEssayImages,
  type EssayPromptRow,
  type GradeEssayResult,
} from "@/tenants/einstein/features/essay/api";
import { cn } from "@/platform/lib/cn";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

type Mode = "text" | "image";

const MAX_PAGES = 4;

function generateUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function EssayPage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<EssayPromptRow[]>([]);
  const [promptId, setPromptId] = useState<string>("");
  const [mode, setMode] = useState<Mode>("text");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeEssayResult | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const ps = await listEssayPrompts();
        setPrompts(ps);
        if (ps[0]) setPromptId(ps[0].id);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Falha ao carregar propostas.");
      }
    })();
  }, []);

  const selectedPrompt = prompts.find((p) => p.id === promptId);

  function onFilesPicked(picked: FileList | null) {
    if (!picked) return;
    const arr = Array.from(picked).slice(0, MAX_PAGES - files.length);
    const valid = arr.filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type) && f.size < 20 * 1024 * 1024);
    if (valid.length < arr.length) {
      toast.error("Aceito apenas JPG/PNG/WebP até 20 MB cada.");
    }
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_PAGES));
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    if (!session || !selectedPrompt) return;
    setSubmitting(true);
    setResult(null);
    try {
      let imagePaths: string[] | undefined;
      if (mode === "image") {
        if (files.length === 0) {
          toast.error("Anexe pelo menos uma foto.");
          setSubmitting(false);
          return;
        }
        const submissionUuid = generateUuid();
        imagePaths = await uploadEssayImages({
          userId: session.user.id,
          submissionUuid,
          files,
        });
      } else if (body.trim().length < 50) {
        toast.error("Redação muito curta. Mínimo 50 caracteres.");
        setSubmitting(false);
        return;
      }

      const collectionExcerpt = (selectedPrompt.collection_texts ?? [])
        .map((t) => `${t.label}: ${t.body}`)
        .join("\n\n")
        .slice(0, 4000);

      const out = await gradeEssay({
        prompt_id: selectedPrompt.id,
        theme: selectedPrompt.theme,
        command: selectedPrompt.command,
        collection_excerpt: collectionExcerpt,
        ...(mode === "text" ? { body } : { image_paths: imagePaths }),
      });
      setResult(out);
      toast.success("Correção pronta.");
    } catch (err) {
      showCorrectionError(toast, err, navigate, "Falha na correção.");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return <EssayResultView result={result} onAgain={() => setResult(null)} onPainel={() => navigate("/painel")} />;
  }

  return (
    <div className="relative overflow-hidden bg-mesh">
      <GradingProgress
        active={submitting}
        steps={[
          "Lendo sua redação…",
          "Avaliando tema e gênero dissertativo-argumentativo…",
          "Conferindo coesão, coerência e norma culta…",
          "Verificando proposta de intervenção…",
          "Montando notas, feedback e plano de reescrita…",
        ]}
      />
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-96 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-20 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-14 md:py-20">
        <header className="animate-fade-up max-w-3xl">
          <Badge variant="accentSolid">
            <PencilLine className="size-3" aria-hidden />
            Redação Vunesp
          </Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Envie sua redação para <span className="text-gradient-accent">correção.</span>
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Pode digitar OU enviar a foto da redação manuscrita (até 4 páginas).
          </p>
        </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
        <Card className="p-7 md:p-8">
          <CardHeader className="p-0">
            <CardTitle>1. Proposta</CardTitle>
            <CardDescription className="mt-1">
              Use uma das propostas oficiais já no banco. Em breve: trazer proposta livre.
            </CardDescription>
          </CardHeader>
          <CardContent className="mt-5 p-0">
            <select
              value={promptId}
              onChange={(e) => setPromptId(e.target.value)}
              className="w-full rounded-md border border-[--color-border] bg-[color:var(--color-card)] p-3 text-[length:var(--text-body-sm)] focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
            >
              {prompts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.year} · {p.theme}
                </option>
              ))}
            </select>

            {selectedPrompt ? (
              <div className="mt-4 rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5">
                <p className="text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                  Comando
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[length:var(--text-body-sm)]">
                  {selectedPrompt.command}
                </p>
                {selectedPrompt.collection_texts && selectedPrompt.collection_texts.length > 0 ? (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      Ver coletânea ({selectedPrompt.collection_texts.length} texto
                      {selectedPrompt.collection_texts.length === 1 ? "" : "s"})
                    </summary>
                    <div className="mt-3 space-y-3 text-[length:var(--text-caption)]">
                      {selectedPrompt.collection_texts.map((t, i) => (
                        <div key={i}>
                          <p className="font-medium">{t.label}</p>
                          <p className="mt-1 whitespace-pre-wrap text-[color:var(--color-muted-foreground)]">
                            {t.body}
                          </p>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            ) : null}

            <div className="mt-8">
              <p className="font-display text-[length:var(--text-h4)] font-semibold leading-[var(--text-h4--line-height)] tracking-[var(--text-h4--letter-spacing)]">
                2. Como vai enviar
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <ModeButton
                  active={mode === "text"}
                  icon={<Type className="size-4" aria-hidden />}
                  label="Digitar"
                  onClick={() => setMode("text")}
                />
                <ModeButton
                  active={mode === "image"}
                  icon={<Camera className="size-4" aria-hidden />}
                  label="Foto manuscrita"
                  onClick={() => setMode("image")}
                />
              </div>

              {mode === "text" ? (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={16}
                  placeholder="Escreva sua redação aqui. Mínimo 50 caracteres."
                  className="mt-4 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-card)] p-4 text-[length:var(--text-body)] font-serif leading-relaxed focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
                />
              ) : (
                <ImageUploader files={files} onPick={onFilesPicked} onRemove={removeFile} />
              )}
            </div>

            <div className="mt-8 sm:flex sm:justify-end">
              <Button
                size="xl"
                variant="gradient"
                onClick={handleSubmit}
                disabled={submitting}
                loading={submitting}
                className="w-full sm:w-auto"
              >
                {submitting ? "Corrigindo…" : "Enviar para correção"}
                <ArrowUpRight className="size-4" aria-hidden />
              </Button>
            </div>
          </CardContent>
        </Card>

        <aside className="space-y-4">
          <Card className="p-6">
            <Badge variant="accent">Como avaliamos</Badge>
            <ul className="mt-4 space-y-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-accent)] shrink-0" /> Rubrica Vunesp · 4 critérios × 5pt = 20pt</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-pink)] shrink-0" /> Detector de modelo pronto e cópia</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-mint)] shrink-0" /> Plano de reescrita por critério</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-yellow-deep)] shrink-0" /> Tempo médio 30–60s</li>
            </ul>
          </Card>
          <Card className="p-6 bg-[color:var(--color-paper)]">
            <Badge variant="pink">Manuscrita</Badge>
            <p className="mt-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] text-pretty">
              Foto clara, página inteira, papel pautado se possível. JPG/PNG/WebP até 20 MB.
              Até 4 páginas. Transcrição é gerada antes da correção.
            </p>
          </Card>
        </aside>
      </div>
      </Container>
    </div>
  );
}

function ModeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-sm border p-3 text-[length:var(--text-body-sm)] transition",
        active
          ? "border-[--color-foreground] bg-[color:var(--color-foreground)] text-[color:var(--color-background)]"
          : "border-[--color-border] text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-foreground)]",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ImageUploader({
  files,
  onPick,
  onRemove,
}: {
  files: File[];
  onPick: (l: FileList | null) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="mt-4 space-y-3">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-[--color-border] bg-[color:var(--color-muted)]/40 p-8 text-center transition hover:bg-[color:var(--color-muted)]/60">
        <Upload className="size-5 text-[color:var(--color-muted-foreground)]" aria-hidden />
        <span className="text-[length:var(--text-body-sm)]">
          Clique para anexar foto{files.length > 0 ? "s adicionais" : "s"} (máx {MAX_PAGES})
        </span>
        <span className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          JPG/PNG/WebP · até 20 MB cada
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
      </label>

      {files.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="relative overflow-hidden rounded-sm border border-[--color-border]"
            >
              <img
                src={URL.createObjectURL(f)}
                alt={`Página ${i + 1}`}
                className="aspect-[3/4] w-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute right-1 top-1 grid size-7 place-items-center rounded-xs bg-[color:var(--color-background)]/90 text-[color:var(--color-foreground)] backdrop-blur"
                aria-label={`Remover página ${i + 1}`}
              >
                <X className="size-3.5" aria-hidden />
              </button>
              <div className="absolute bottom-1 left-1 rounded-xs bg-[color:var(--color-background)]/90 px-1.5 py-0.5 text-[length:var(--text-caption)] backdrop-blur">
                pág {i + 1}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function EssayResultView({
  result,
  onAgain,
  onPainel,
}: {
  result: GradeEssayResult;
  onAgain: () => void;
  onPainel: () => void;
}) {
  const total = result.scores.total;
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-60 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-32 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-14 md:py-20">
        <header className="animate-fade-up max-w-3xl">
          <Badge variant="accentSolid">Correção concluída</Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            <span data-tabular="true" className="text-gradient-accent">
              {total.toFixed(1)}
            </span>{" "}
            de 20 pontos.
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Rubrica Vunesp · {result.source === "image" ? "manuscrita transcrita pela IA" : "texto digitado"} · confiança{" "}
            <span data-tabular="true" className="font-semibold">
              {(result.confidence * 100).toFixed(0)}%
            </span>
          </p>
        </header>

        <div className="mt-10 grid gap-4 md:grid-cols-4">
          <ScoreCard label="Tema" score={result.scores.tema} max={5} tone="violet" />
          <ScoreCard label="Estrutura" score={result.scores.estrutura} max={5} tone="pink" />
          <ScoreCard label="Língua" score={result.scores.lingua} max={5} tone="mint" />
          <ScoreCard label="Coesão" score={result.scores.coesao} max={5} tone="yellow" />
        </div>

        {result.model_risk.length > 0 ? (
          <Card className="mt-8 border-2 border-[--color-warning]/50 bg-[color:var(--color-warning-soft)] p-6">
            <p className="flex items-center gap-2 font-display text-[length:var(--text-h4)] font-semibold text-[color:var(--color-warning)]">
              <AlertCircle className="size-5" aria-hidden />
              Riscos detectados
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {result.model_risk.map((r) => (
                <Badge key={r} variant="warning">
                  {r.replace(/_/g, " ")}
                </Badge>
              ))}
            </div>
          </Card>
        ) : null}

        <Card className="mt-8 p-7 md:p-8">
          <CardHeader className="p-0">
            <Badge variant="accent">Feedback por critério</Badge>
          </CardHeader>
          <CardContent className="mt-4 p-0">
            <p className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed text-pretty">
              {result.feedback}
            </p>
          </CardContent>
        </Card>

        <Card className="mt-6 p-7 md:p-8 bg-[color:var(--color-paper)]">
          <CardHeader className="p-0">
            <Badge variant="mint">Plano de reescrita</Badge>
            <CardDescription className="mt-2">
              Os passos concretos para ganhar pontos na próxima versão.
            </CardDescription>
          </CardHeader>
          <CardContent className="mt-4 p-0">
            <p className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed text-pretty">
              {result.rewrite_plan}
            </p>
          </CardContent>
        </Card>

        {result.transcription ? (
          <details className="mt-6">
            <summary className="cursor-pointer text-[length:var(--text-body-sm)] font-semibold text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-accent)]">
              Ver transcrição da redação manuscrita
            </summary>
            <Card className="mt-3 p-6">
              <p className="whitespace-pre-wrap font-serif text-[length:var(--text-body)] leading-relaxed text-pretty">
                {result.transcription}
              </p>
            </Card>
          </details>
        ) : null}

        <div className="mt-10 flex flex-wrap gap-3">
          <Button size="xl" variant="gradient" onClick={onAgain}>
            Corrigir outra
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>
          <Button size="xl" variant="outline" onClick={onPainel}>
            Voltar ao painel
          </Button>
        </div>
      </Container>
    </div>
  );
}

type ScoreTone = "violet" | "pink" | "mint" | "yellow";
const SCORE_TONE_BADGE: Record<ScoreTone, "accent" | "pink" | "mint" | "yellow"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
};
const SCORE_TONE_BAR: Record<ScoreTone, string> = {
  violet: "bg-[color:var(--color-accent)]",
  pink: "bg-[color:var(--color-pink)]",
  mint: "bg-[color:var(--color-mint)]",
  yellow: "bg-[color:var(--color-yellow-deep)]",
};

function ScoreCard({
  label,
  score,
  max,
  tone,
}: {
  label: string;
  score: number;
  max: number;
  tone: ScoreTone;
}) {
  const pct = Math.round((score / max) * 100);
  return (
    <Card className="p-5 lift">
      <Badge variant={SCORE_TONE_BADGE[tone]}>{label}</Badge>
      <p
        data-tabular="true"
        className="mt-4 font-display text-[length:var(--text-h1)] font-bold leading-none tracking-[var(--text-h1--letter-spacing)]"
      >
        {score.toFixed(1)}
        <span className="text-[length:var(--text-body-sm)] font-normal text-[color:var(--color-muted-foreground)]">
          {" "}/ {max}
        </span>
      </p>
      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--color-muted)]">
        <div
          className={`h-full rounded-full transition-all duration-700 ${SCORE_TONE_BAR[tone]}`}
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
    </Card>
  );
}
